import api from "@/components/customAxios/Axios";
import { useToast } from "@/components/toast";
import useBreadCrumb from "@/context/BreadCrumbContext";
import { useLoader } from "@/context/loaderContext";
import { useEffect, useState } from "react";
import useRelativeTime from "@/hooks/useRelativeTime";
import { useLocation, useParams } from "react-router-dom";
import { MdCancelPresentation } from "react-icons/md";
import { LiaSave } from "react-icons/lia";

type ExtractedInfoDetails = {
	address: string;
	behaviour_skills: string;
	candidate_name: string;
	date_of_birth: string;
	email_id: string;
	github_id: string;
	job_details: {
		job_description: string;
		role: string;
		job_id: string;
	};
	resume_upload_date: string;
	linkedin_id: string;
	nationality: string;
	phone_number: string;
	tech_skills: string[];
	total_experience: string;
	resume_skills_match: any;
	overall_ai_explanation: string
};

type Data = {
	extracted_info_details: ExtractedInfoDetails;
};


const CandidateResume = ({ }: {}) => {
	const params = useParams()
	const location = useLocation();
	const pathname = location.pathname;
	const { setBreadCrumb } = useBreadCrumb();
	const { candidateId } = useParams();
	const { setLoading } = useLoader();
	const toast = useToast();
	const [candidateDetails, setCandidateDetails] = useState<Data>();
	const [isEditingEmail, setIsEditingEmail] = useState(false);
	const [newEmail, setNewEmail] = useState("");

	const getCandidateDetails = async () => {
		setLoading(true);
		try {
			const res = await api.get(`extracted_info`, {
				params: {
					candidate_id: candidateId,
				},
			});
			if (res.status === 200) {
				setCandidateDetails(res.data);
			}
		} catch (err: any) {
			setLoading(false);
			toast.error({
				type: "background",
				duration: 3000,
				status: "Error",
				title: "Failed to fetch candidate details",
				description: err.response?.data?.error || err.response?.data?.message || "Unable to fetch candidate information. Please try again later.",
				open: true,
			});
		} finally {
			setLoading(false);
		}
	};
	const overAllJustification = (data: string) => {

		const parsedList: string[] = data
			.slice(1, -1) // Remove the enclosing {}
			.split('","') // Split by the quote-comma-quote delimiter
			.map((item: any) => item.replace(/^"|"$/g, "")); // Remove remaining quotes
		return parsedList
	}
	useEffect(() => {
		const isRespectiveDashboard = pathname.includes('respective-dashboard');
		setBreadCrumb({
			mainLabel: {
				label: isRespectiveDashboard ? "Job  Applicants" : "Applicants",
				link: isRespectiveDashboard ? `/respective-dashboard/${params.id}` : "/applicants",
			},
			currentLabel: "Resume",
		});
	}, []);

	useEffect(() => {
		getCandidateDetails();
	}, []); // Empty dependency array to run only once on mount

	const handleEmailUpdate = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault()
		if (!newEmail || !candidateId) return;

		setLoading(true);
		try {
			const response = await api.put(`update_candidate_contact`, {
				candidate_id: candidateId,
				email: newEmail
			});

			if (response.status === 200) {
				setCandidateDetails(prev => {
					if (!prev) return prev;
					return {
						...prev,
						extracted_info_details: {
							...prev.extracted_info_details,
							email_id: newEmail
						}
					};
				});

				setIsEditingEmail(false);
				toast.success({
					type: "background",
					duration: 3000,
					status: "Success",
					title: "Email updated successfully",
					description: "The candidate's email has been updated.",
					open: true,
				});
			}
		} catch (err: any) {
			toast.error({
				type: "background",
				duration: 3000,
				status: "Error",
				title: "Failed to update email",
				description: err.response?.data?.error || err.response?.data?.message || "Unable to update email address. Please try again later.",
				open: true,
			});
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="bg-white md:p-4 rounded-xl w-full min-h-[85vh]">
			{candidateDetails ? (
				<>
					<div className="flex items-center gap-x-1 mb-4 p-0">
						<button onClick={() => window.history.back()}>
							<svg
								width="20"
								height="18"
								viewBox="0 0 14 14"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M10.0154 0.733154L4.23065 6.51518C3.93728 6.79466 3.93728 7.23137 4.23065 7.51085L10.0155 13.2928"
									stroke="#7F7F7F"
									strokeMiterlimit="2.613"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						</button>
						<h2 className="text-2xl font-bold   ">Resume</h2>
					</div>

					<div className="px-4 pb-3">
						{/* Candidate Header */}
						<div className="border-b pb-3 space-y-5  ">
							<h2 className="text-2xl font-bold   ">
								{candidateDetails?.extracted_info_details.candidate_name}
							</h2>
							<div className="  flex gap-4 flex-wrap">
								<p className="flex items-center gap-2">
									<svg
										width="20"
										height="24"
										viewBox="0 0 20 24"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											d="M17.4961 4.18071C16.4273 2.88953 15.043 1.89629 13.4774 1.29748C11.9119 0.698676 10.2182 0.514567 8.56049 0.762891C6.90282 1.01121 5.33742 1.68352 4.01606 2.7148C2.69469 3.74607 1.66218 5.10126 1.01862 6.64895C0.283569 8.4237 0.0914761 10.3767 0.466646 12.2607C0.841815 14.1446 1.76739 15.8749 3.12624 17.2327C3.27911 17.3852 3.48641 17.4708 3.70254 17.4707C3.91867 17.4707 4.12592 17.3848 4.2787 17.2323C4.43149 17.0797 4.51728 16.8729 4.51722 16.6572C4.51715 16.4415 4.43124 16.2346 4.27837 16.0822C3.14592 14.9526 2.37453 13.5122 2.06207 11.9435C1.74961 10.3748 1.91017 8.74868 2.52337 7.27142C3.13644 5.79337 4.17393 4.53029 5.50472 3.64175C6.8355 2.75321 8.39984 2.27906 9.99999 2.27939C11.6004 2.27874 13.1651 2.7526 14.4962 3.64116C15.8273 4.52972 16.8651 5.79311 17.4782 7.27142C18.0914 8.74816 18.2519 10.3738 17.9394 11.9419C17.627 13.51 16.8556 14.9499 15.7232 16.0788C15.6474 16.1544 15.5873 16.2442 15.5463 16.343C15.5052 16.4418 15.4841 16.5477 15.4841 16.6546C15.484 16.7616 15.5051 16.8676 15.5461 16.9665C15.5871 17.0653 15.6471 17.155 15.7229 17.2307C15.7987 17.3063 15.8886 17.3663 15.9876 17.4072C16.0866 17.4482 16.1927 17.4693 16.2999 17.4693C16.407 17.4694 16.5132 17.4483 16.6122 17.4074C16.7112 17.3665 16.8012 17.3067 16.877 17.2311C18.5857 15.5232 19.5959 13.2393 19.7101 10.8261C19.8243 8.41292 19.0342 6.04393 17.4945 4.18229L17.4961 4.18071Z"
											fill="#848484"
										/>
										<path
											d="M3.69007 15.8318C3.5274 15.8311 3.36828 15.8793 3.23329 15.9701C3.09831 16.0608 2.99367 16.1901 2.93292 16.341C2.87218 16.4919 2.85813 16.6575 2.89258 16.8165C2.92704 16.9754 3.00842 17.1204 3.12619 17.2326L8.72757 22.8241C9.06682 23.1582 9.52383 23.3454 9.99994 23.3454C10.476 23.3454 10.9331 23.1582 11.2723 22.8241L16.8737 17.2326C17.0265 17.0799 17.1123 16.8727 17.1123 16.6566C17.1123 16.4405 17.0265 16.2333 16.8737 16.0805C16.7209 15.9278 16.5137 15.8419 16.2976 15.8419C16.0816 15.8419 15.8743 15.9278 15.7216 16.0805L10.1234 21.6736C10.0454 21.75 9.95444 21.75 9.87644 21.6736L4.27832 16.0805C4.202 16.0018 4.11055 15.9393 4.00945 15.8969C3.90835 15.8544 3.79971 15.8311 3.69007 15.8318Z"
											fill="#848484"
										/>
										<path
											d="M10.0293 5.52783C7.34642 5.52783 5.1543 7.71996 5.1543 10.4028C5.1543 13.0841 7.34642 15.2778 10.0293 15.2778C12.7105 15.2778 14.9043 13.0841 14.9043 10.4028C14.9043 7.71996 12.7105 5.52783 10.0293 5.52783ZM10.0293 7.15601C10.4561 7.15429 10.8791 7.23704 11.2738 7.3996C11.6685 7.56215 12.0271 7.80136 12.3289 8.1032C12.6308 8.40503 12.8699 8.7636 13.0324 9.15829C13.195 9.55298 13.2778 9.97598 13.276 10.4028C13.276 12.2066 11.8314 13.6528 10.0293 13.6528C8.22555 13.6528 6.78255 12.2066 6.78255 10.4028C6.78083 9.97598 6.86363 9.55298 7.02619 9.15829C7.18874 8.7636 7.42783 8.40503 7.72966 8.1032C8.0315 7.80136 8.3901 7.56215 8.78479 7.3996C9.17948 7.23704 9.60245 7.15429 10.0293 7.15601Z"
											fill="#848484"
										/>
									</svg>

									{candidateDetails?.extracted_info_details.address}
								</p>
								<p className="flex items-center gap-2">
									<svg
										width="22"
										height="23"
										viewBox="0 0 22 23"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											d="M2.81496 3.41202C2.93314 3.23908 3.04731 3.07055 3.15551 2.90895C4.13639 1.44411 6.17971 1.32415 7.26471 2.66047L8.51379 4.19884C9.30116 5.1686 9.08859 6.63016 8.05999 7.31904L7.76084 7.51939C7.19469 7.89855 6.98486 8.64917 7.26841 9.28094C8.52129 12.0724 10.695 14.314 13.4019 15.6061C14.0145 15.8985 14.7424 15.6821 15.11 15.0982L15.3043 14.7897C15.9723 13.729 17.3896 13.5098 18.33 14.3218L19.8217 15.6099C21.1175 16.7288 21.0013 18.836 19.5808 19.8475C19.4241 19.9591 19.2606 20.0768 19.093 20.1987C16.891 21.7991 13.9781 21.8981 11.7586 20.3722C8.14966 17.8909 5.05284 14.6973 2.64674 10.9756C1.16696 8.68666 1.26301 5.68281 2.81496 3.41202Z"
											stroke="#848484"
											strokeWidth="1.5"
										/>
									</svg>

									{candidateDetails?.extracted_info_details.phone_number}
								</p>
								<div className="flex items-center gap-2">
									<svg
										width="24"
										height="18"
										viewBox="0 0 24 18"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											d="M20.9375 0.875H3.0625C2.41603 0.875 1.79605 1.13181 1.33893 1.58893C0.881807 2.04605 0.625 2.66603 0.625 3.3125V14.6875C0.625 15.334 0.881807 15.954 1.33893 16.4111C1.79605 16.8682 2.41603 17.125 3.0625 17.125H20.9375C21.584 17.125 22.204 16.8682 22.6611 16.4111C23.1182 15.954 23.375 15.334 23.375 14.6875V3.3125C23.375 2.66603 23.1182 2.04605 22.6611 1.58893C22.204 1.13181 21.584 0.875 20.9375 0.875ZM3.0625 2.5H20.9375C21.0918 2.50138 21.2425 2.54666 21.372 2.63054C21.5015 2.71443 21.6044 2.83345 21.6687 2.97369L13.4861 9.26975C13.06 9.59746 12.5375 9.77515 12 9.77515C11.4625 9.77515 10.94 9.59746 10.5139 9.26975L2.33125 2.97369C2.39558 2.83345 2.49852 2.71443 2.62801 2.63054C2.75751 2.54666 2.90821 2.50138 3.0625 2.5ZM20.9375 15.5H3.0625C2.84701 15.5 2.64035 15.4144 2.48798 15.262C2.3356 15.1097 2.25 14.903 2.25 14.6875V4.96269L9.52269 10.5576C10.2336 11.1032 11.1047 11.399 12.0008 11.399C12.897 11.399 13.7681 11.1032 14.4789 10.5576L21.75 4.96269V14.6875C21.75 14.903 21.6644 15.1097 21.512 15.262C21.3597 15.4144 21.153 15.5 20.9375 15.5Z"
											fill="#848484"
										/>
									</svg>
									{isEditingEmail ? (
										<form className="flex items-center gap-2" onSubmit={handleEmailUpdate}>
											<input
												required
												type="email"
												value={newEmail}
												onChange={(e) => setNewEmail(e.target.value)}
												className="border rounded px-2 py-1 md:w-[500px]"
												placeholder="Enter new email"
											/>
											<div className="relative group">

												<button

													className="cursor-pointer p-1 text-sm rounded-md hover:scale-110 transition duration-300 ease-in-out flex items-center justify-center"
												>
													<LiaSave size={20} />
												</button>
												<p className="absolute right-1  w-[50px] text-center  ml-2 px-2 py-1 text-sm bg-black text-white rounded -z-10 group-hover:z-50 transition duration-300 ease-in-out">
													Save
												</p>
											</div>
											<div className="relative group">

												<button
													onClick={() => {
														setIsEditingEmail(false);
														setNewEmail("");
													}}
													className="text-red-500 cursor-pointer p-1 text-sm rounded-md hover:scale-110 transition duration-300 ease-in-out flex items-center justify-center"
												>
													<MdCancelPresentation size={20} />
												</button>
												<p className="absolute right-1  w-[7	0px] text-center  ml-2 px-2 py-1 text-sm bg-black text-white rounded -z-10 group-hover:z-50 transition duration-300 ease-in-out">
													Cancel
												</p>
											</div>

										</form>
									) : (
										<div className="flex items-center gap-2">
											<span>{candidateDetails?.extracted_info_details.email_id}</span>
											<div className="relative group">

												<button
													onClick={() => {
														setIsEditingEmail(true);
														setNewEmail(candidateDetails?.extracted_info_details.email_id || "");
													}}
													className="text-gray-500 hover:text-gray-600 mt-2"
												>
													<svg
														width="16"
														height="16"
														viewBox="0 0 24 24"
														fill="none"
														xmlns="http://www.w3.org/2000/svg"
													>
														<path
															d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
															stroke="currentColor"
															strokeWidth="1.5"
															strokeLinecap="round"
															strokeLinejoin="round"
														/>
													</svg>
												</button>
												<p className="absolute right-1  w-[50px] text-center  ml-2 px-2 py-1 text-sm bg-black text-white rounded -z-10 group-hover:z-50 transition duration-300 ease-in-out">
													Edit
												</p>
											</div>

										</div>
									)}
								</div>
							</div>

							<p className="flex gap-1 items-center justify-center bg-[#C3EEBC] text-[#2F6C57] font-semibold rounded-2xl p-2 w-max">
								<span>
									<svg
										width="29"
										height="29"
										viewBox="0 0 29 29"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											d="M14.964 24.3601C9.54099 24.3601 5.16199 19.9521 5.16199 14.5581C5.16199 9.1351 9.56999 4.7561 14.964 4.7561C20.358 4.7561 24.766 9.1641 24.766 14.5581C24.766 19.9521 20.387 24.3601 14.964 24.3601ZM14.964 5.8001C10.15 5.8001 6.20599 9.7151 6.20599 14.5291C6.20599 19.3431 10.121 23.2871 14.964 23.2871C19.807 23.2871 23.722 19.3721 23.722 14.5291C23.722 9.7151 19.778 5.8001 14.964 5.8001Z"
											fill="#2F6C57"
										/>
										<path
											d="M18.444 18.531C18.299 18.531 18.183 18.473 18.067 18.386L14.587 14.906C14.5 14.819 14.442 14.674 14.442 14.529V8.17801C14.442 7.88801 14.674 7.65601 14.964 7.65601C15.254 7.65601 15.486 7.88801 15.486 8.17801V14.326L18.792 17.632C18.995 17.835 18.995 18.183 18.792 18.386C18.705 18.473 18.56 18.531 18.444 18.531Z"
											fill="#2F6C57"
										/>
									</svg>
								</span>
								<span>
									{" "}
									Applied{" "}
									{useRelativeTime(
										candidateDetails?.extracted_info_details.resume_upload_date
									)}{" "}
								</span>
							</p>
						</div>

						{/* Technical Skills */}
						<div className="my-6">
							{/* <div className="flex justify-between">
						<h3 className="text-lg font-bold  mb-2">Job Role</h3>
						<div className=" text-center">
							<Link
								to={`/respective-dashboard/${candidateDetails?.extracted_info_details.job_details.job_id}`}
								className="text-indigo-500 hover:underline"
							>
								See Job Details
							</Link>
						</div>
					</div> */}
							{/* <p className="   mb-4">
								<strong className="text-lg font-bold  mb-2.5 block">
									Job Role
								</strong>{" "}
								{candidateDetails?.extracted_info_details.job_details.role ||
									"N/A"}
							</p> */}

							{/* <div className="text-justify   mb-4">
								<strong className="text-lg font-bold  mb-2.5 block">
									Job Description
								</strong>{" "}
								<pre className="h-52 font-main whitespace-pre-wrap break-words overflow-y-auto border rounded-xl p-2 text-justify">
									{
										candidateDetails?.extracted_info_details.job_details
											.job_description
									}
								</pre> */}

							{/* {candidateDetails?.extracted_info_details.job_details
							.job_description ? (
							<>
								{showFullDescription
									? candidateDetails.extracted_info_details.job_details
											.job_description
									: `${candidateDetails.extracted_info_details.job_details.job_description.slice(
											0,
											100
									  )}...`}
								<button
									onClick={() => setShowFullDescription(!showFullDescription)}
									className="ml-2 text-indigo-500 hover:underline focus:outline-none"
								>
									{showFullDescription ? "Show Less" : "Read More"}
								</button>
							</>
						) : (
							"N/A"
						)} */}
							{/* </div> */}
						</div>
						<div className="text-xl font-semibold  mb-1">Summary</div>
						<ul className="list-none pl-6 space-y-1 text-gray-700 mb-5">
							{overAllJustification(candidateDetails.extracted_info_details.overall_ai_explanation).map((point, index) => (
								<li key={index}>{point}</li>
							))}
						</ul>



						{/* Technical Skills */}
						<div className="mb-4">
							<h3 className="text-lg font-bold  mb-2.5">Technical Skills</h3>
							<div className="flex flex-wrap gap-y-4 gap-x-2">
								{candidateDetails?.extracted_info_details.tech_skills.map(
									(skill, index) => (
										<span
											key={index}
											className="bg-[#EEEEEE] rounded-full px-3 py-1 text-sm font-medium"
										>
											{skill}
										</span>
									)
								)}
							</div>
						</div>

						{/* Behavioral Skills */}
						<div className="mb-4">
							<h3 className="text-lg font-bold  mb-2.5">Behavioral Skills</h3>
							{Array.isArray(
								candidateDetails?.extracted_info_details?.behaviour_skills
							) ? (
								<div className="flex flex-wrap gap-2">
									{candidateDetails?.extracted_info_details.behaviour_skills.map(
										(skill, index) => (
											<span
												key={index}
												className="bg-[#EEEEEE] rounded-full px-3 py-1 text-sm font-medium"
											>
												{skill}
											</span>
										)
									)}
								</div>
							) : (
								<p className="text-sm text-gray-500">
									No behavioral skills listed.
								</p>
							)}
						</div>

						{/* Additional Information */}
						<div>
							<h3 className="text-lg font-bold  mb-2.5">
								Additional Information
							</h3>
							<ul className="space-y-2.5">
								<li className=" ">
									<strong>Date of Birth:</strong>{" "}
									{candidateDetails?.extracted_info_details.date_of_birth ||
										"N/A"}
								</li>
								<li className="">
									<strong>Nationality:</strong>{" "}
									{candidateDetails?.extracted_info_details.nationality ||
										"N/A"}
								</li>
								<li className="">
									<strong>LinkedIn:</strong>{" "}
									<a
										href={`https://${candidateDetails?.extracted_info_details.linkedin_id}`}
										target="_blank"
										rel="noopener noreferrer"
										className="text-blue-500 hover:underline"
									>
										{candidateDetails?.extracted_info_details.linkedin_id}
									</a>
								</li>
								<li className="">
									<strong>GitHub:</strong>{" "}
									<a
										href={`https://${candidateDetails?.extracted_info_details.github_id}`}
										target="_blank"
										rel="noopener noreferrer"
										className="text-blue-500 hover:underline"
									>
										{candidateDetails?.extracted_info_details.github_id}
									</a>
								</li>
								<li className="">
									<strong>Total Experience:</strong>{" "}
									{candidateDetails?.extracted_info_details.total_experience}
								</li>
							</ul>
						</div>
					</div>
				</>
			) : (
				<div className=" font-semibold text-3xl  mt-5">
					<div className="text-center">No Data Found For The Candidate </div>
				</div>
			)}
			{/* Job Details Link */}
		</div>
	);
};

export default CandidateResume;
